/**
 * useAuthRequest Hook
 * 
 * Manages PAR-Lite (Pushed Authorization Request) context for the Identity module.
 * Handles resolving auth requests and providing organization context during login.
 * 
 * @see docs/architecture/PAR_LITE.md for PAR-Lite design
 */

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

interface OrganizationContext {
  id: string;
  name: string;
  subdomain: string;
  logoUrl?: string;
}

interface TenantSupabaseConfig {
  url: string;
  anonKey: string;
}

interface AuthRequestContext {
  valid: boolean;
  organization: OrganizationContext | null;
  returnPath: string | null;
  tenantSupabase: TenantSupabaseConfig | null;
  nonce: string | null;
  redirectUri: string | null;
}

interface UseAuthRequestReturn {
  context: AuthRequestContext | null;
  loading: boolean;
  error: string | null;
  resolveRequest: (requestUri: string) => Promise<boolean>;
  clearContext: () => void;
}

// Platform Supabase for resolving auth requests
const PLATFORM_URL = import.meta.env.VITE_SUPABASE_URL;
// Support both variable names for compatibility across deployments
const PLATFORM_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

const platformClient = PLATFORM_URL && PLATFORM_ANON_KEY
  ? createClient(PLATFORM_URL, PLATFORM_ANON_KEY)
  : null;

export function useAuthRequest(): UseAuthRequestReturn {
  const [context, setContext] = useState<AuthRequestContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check for request_uri in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const requestUri = urlParams.get('request_uri');
    
    if (requestUri && requestUri.startsWith('urn:cnvrgnx:par:')) {
      console.log('[useAuthRequest] Found request_uri in URL, resolving...');
      resolveRequest(requestUri);
    }
  }, []);

  const resolveRequest = useCallback(async (requestUri: string): Promise<boolean> => {
    if (!platformClient) {
      setError('Platform client not configured');
      return false;
    }

    if (!requestUri || !requestUri.startsWith('urn:cnvrgnx:par:')) {
      setError('Invalid request URI format');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('[useAuthRequest] Resolving auth request:', requestUri);

      const { data, error: invokeError } = await platformClient.functions.invoke('resolve-auth-request', {
        body: { request_uri: requestUri },
      });

      if (invokeError) {
        console.error('[useAuthRequest] Error resolving request:', invokeError);
        setError(invokeError.message || 'Failed to resolve auth request');
        setLoading(false);
        return false;
      }

      if (!data?.valid) {
        const errorMsg = data?.error || 'Auth request is invalid or expired';
        console.error('[useAuthRequest] Invalid request:', errorMsg);
        setError(errorMsg);
        setLoading(false);
        return false;
      }

      // Build context from response
      const authContext: AuthRequestContext = {
        valid: true,
        organization: data.organization ? {
          id: data.organization.id,
          name: data.organization.name,
          subdomain: data.organization.subdomain,
          logoUrl: data.organization.logo_url,
        } : null,
        returnPath: data.return_path || null,
        tenantSupabase: data.tenant_supabase ? {
          url: data.tenant_supabase.url,
          anonKey: data.tenant_supabase.anon_key,
        } : null,
        nonce: data.nonce || null,
        redirectUri: data.redirect_uri || null,
      };

      setContext(authContext);
      setLoading(false);

      console.log('[useAuthRequest] Resolved context:', {
        org: authContext.organization?.subdomain,
        hasTenantSupabase: !!authContext.tenantSupabase,
      });

      return true;
    } catch (err) {
      console.error('[useAuthRequest] Exception resolving request:', err);
      setError(err instanceof Error ? err.message : 'Failed to resolve auth request');
      setLoading(false);
      return false;
    }
  }, []);

  const clearContext = useCallback(() => {
    setContext(null);
    setError(null);
  }, []);

  return {
    context,
    loading,
    error,
    resolveRequest,
    clearContext,
  };
}

/**
 * Get the request_uri from the current URL if present.
 */
export function getRequestUriFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('request_uri');
}

/**
 * Check if we're in a PAR-Lite authentication flow.
 */
export function isParLiteFlow(): boolean {
  const requestUri = getRequestUriFromUrl();
  return !!requestUri && requestUri.startsWith('urn:cnvrgnx:par:');
}
